import {TooltipRow} from 'common/components/chart-tooltip/types';
import type {FunnelStep} from 'common/report/funnels/models/types';

/** Information used to display individual bars */
export interface BarDescriptor {
  /** the annotation details for this bar */
  annotation?: {
    value: string;
  };
  /** the color this bar should be displayed in */
  color: string;
  /** the number of items in this bucket */
  count: number;
  /** whether this bar should emit an event on clicks */
  isClickable: boolean;
  /** whether this bar is currently selected */
  isSelected: boolean;
  /** label for this bar */
  label: string;
  /** identifier for this bar */
  name: string | symbol;
  /** percentage value of this bar for relative view */
  percentage?: number;
  /** Styling for bar */
  style?: BarStyle;
}

/** Bar Currently selected used for Cohort Creation */
export interface SelectedBar {
  segment: BarDescriptor;
  rowLabel: string;
  queryIndex?: number;
  clientX: number;
  clientY: number;
}

/** Top Level grouping for bars representing the interval (ie 1-2 hours, 1 freq interval)*/
export interface Bucket {
  end?: number;
  label: string;
  segments?: BarDescriptor[];
  start?: number;
  text?: string;
  /** original index of this bucket in the query & response  */
  queryIndex?: number;
}

/** Object to more easily pass around formatting methods */
export interface TextLabelFormatter {
  getBucketLabel: (bucketStart: number, bucketEnd?: number, precision?: number) => string;
  getMedianFormatter: (value: number) => string;
  getToolTipText?: (count: number, percentage: number, bucketLabel: string) => TooltipRow[];
  getMedianToolTipText?: (value: string) => TooltipRow[];
}

export enum BarStyle {
  Solid = `solid`,
  Stripe = `stripe`,
}

/** Information for the current segment used for displaying on bar-chart */
export type SelectedSegmentStep = {
  segmentKey: string;
  segmentName: string;
  step: FunnelStep;
  color: string;
  style?: BarStyle;
};

export interface BucketDefinition {
  /** Min numeric value to bucket */
  min: number;
  /** Max numeric value to bucket */
  max: number;
  /** Bucket size for bucketing */
  bucketSize: number;
  /** Offset to start bucketing at. */
  offset: number;
}

export enum TimeBucketKey {
  START = `time_buckets_from_start`,
  PREV = `time_buckets_from_prev`,
}

export enum PercentileKey {
  START = `percentiles_from_start`,
  PREV = `percentiles_from_prev`,
}

/** Defines an interface to perform bucketing on numeric values. Used by TimeBuckets */
export interface TimeBucketDefinition extends BucketDefinition {
  /** if the user has drilled down into specific buckets */
  isDrilledDown: boolean;
}
